"use client";

import React, { useEffect, useRef, useState } from "react";
import { ClipboardCheck, PackageOpen, SearchCheck, Sparkles, ArrowRight } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const steps = [{
  title: "Consult",
  description: "We begin with a quiet conversation about your daily rhythms, identifying how Apple HomeKit can simplify your life without adding complexity.",
  icon: ClipboardCheck
}, {
  title: "Setup",
  description: "Our experts curate and configure your smart hub with a focus on privacy and seamless integration into your home's existing aesthetic.",
  icon: PackageOpen
}, {
  title: "Automate",
  description: "We craft invisible routines that respond to your presence, from morning light transitions to secure evening lockdowns, all within the Apple ecosystem.",
  icon: Sparkles
}, {
  title: "Enjoy",
  description: "Experience the art of simple living. Your home now works for you, providing peace of mind and effortless control through the Home app.",
  icon: SearchCheck
}];
export default function ThePerfectProtocol() {
  const sectionRef = useRef<HTMLElement>(null);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const totalScrollableDistance = rect.height;
      const currentScrollPosition = windowHeight / 2 - rect.top;
      let percentage = currentScrollPosition / totalScrollableDistance * 100;
      percentage = Math.max(0, Math.min(100, percentage));
      setProgress(percentage);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="222853" id="your-path-to-automation" ref={sectionRef} className="bg-background text-foreground relative min-h-screen">
      {}
      <div className="sticky top-0 z-50 bg-background/70 backdrop-blur-xl border-b border-border/50 py-4 px-6 shadow-sm">
        <div className="max-w-4xl mx-auto flex flex-col gap-3">
          <div className="flex justify-between items-center">
            <h2 className="text-lg md:text-xl font-default font-semibold text-primary tracking-tight">
              Your Path to Automation
            </h2>
            <span className="text-sm font-mono text-muted-foreground">
              {Math.round(progress)}%
            </span>
          </div>
          <Progress value={progress} className="h-1 bg-muted" />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-24 md:py-32">
        <div className="text-center mb-24">
          <h3 className="text-3xl md:text-6xl font-default font-bold text-primary mb-6 tracking-tighter">
            The Art of <br className="hidden md:block" /> Simple Living.
          </h3>
          <p className="text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto font-default leading-relaxed">
            Discover the Moza Method: a refined four-step journey to transforming your space into a responsive, intelligent sanctuary.
          </p>
        </div>

        {}
        <div className="relative border-l-2 border-secondary ml-4 md:ml-12 space-y-32 pb-24">
          {steps.map((step, i) => {
          const Icon = step.icon;
          return <div key={i} data-index={i} className="relative pl-8 md:pl-20 min-h-[30vh] flex flex-col justify-center group">
                {}
                <div className="absolute -left-[11px] top-1/2 -translate-y-1/2 size-5 rounded-full bg-background border-4 border-secondary transition-transform duration-500 group-hover:scale-125 group-hover:border-primary" />
                
                {}
                <span className="absolute -left-8 md:-left-12 top-1/2 -translate-y-1/2 text-[8rem] md:text-[14rem] font-serif font-bold text-secondary/20 select-none leading-none pointer-events-none transition-colors duration-500 group-hover:text-secondary/40">
                  {i + 1}
                </span>

                <Card className="relative z-10 border-none shadow-none bg-transparent overflow-visible">
                  <CardHeader className="px-0 pt-0 pb-4">
                    <div className="flex items-center gap-4 mb-2">
                      <div className="p-3 rounded-2xl bg-secondary text-secondary-foreground shadow-sm transition-colors duration-300 group-hover:bg-primary group-hover:text-primary-foreground">
                        <Icon className="size-6" />
                      </div>
                      <CardTitle className="text-2xl md:text-4xl font-default font-bold text-primary tracking-tight">
                        {step.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="px-0">
                    <p className="text-muted-foreground text-lg leading-relaxed max-w-xl font-default">
                      {step.description}
                    </p>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

        <div className="mt-12 text-center relative z-20">
          <Button asChild size="lg" className="rounded-full px-10 py-6 bg-primary text-primary-foreground hover:opacity-90 transition-opacity shadow-lg">
            <Link to="#">
              Experience the Method
              <ArrowRight className="ml-2 size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}